import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Pie, Bar } from 'react-chartjs-2';
import { OPCIONES_BASE, EJES_BASE, agruparEnOtros, datasetBarrasSimple } from '../../utils/graficos';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
// Servicio de reportes
import * as reportesARTService from '../../services/reportes/reportesARTService';
import { usePDFReportes } from '../../components/ReportesPDF';
import PageHeader from '../../components/ui/PageHeader';
import '../../style/pages/HomePage.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { formatearFechaMientrasEscribe } from '../../utils/validacionesCaratula';



const BASE_OPTIONS = OPCIONES_BASE;
// Barras de una sola medida: sin leyenda (el título ya la nombra) y ejes del sistema.
const OPCIONES_BARRAS = { ...OPCIONES_BASE, plugins: { ...OPCIONES_BASE.plugins, legend: { display: false } }, scales: EJES_BASE };
const OPCIONES_BARRAS_H = { ...OPCIONES_BARRAS, indexAxis: 'y', scales: { x: EJES_BASE.y, y: EJES_BASE.x } };

const convertirTextoAFecha = (texto) => {
    if (!texto?.trim()) return null;
    const partes = texto.replace(/[^\d/]/g,'').split('/');
    if (partes.length===3) {
        const [dia,mes,anio] = partes.map(Number);
        if (dia>0&&dia<=31&&mes>0&&mes<=12&&anio>=1900&&anio<=2100) {
            const f = new Date(anio,mes-1,dia);
            if (!isNaN(f.getTime())&&f.getDate()===dia&&f.getMonth()===mes-1) return f.toISOString().split('T')[0];
        }
    }
    return null;
};

function ReportesTramitesART() {
    const navigate = useNavigate();


    // ESTADOS QUE QUEDAN (UI + Datos procesados)
    const [resumen, setResumen] = useState(null);
    const [porEstado, setPorEstado] = useState([]);
    const [tiempoPromedio, setTiempoPromedio] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros
    const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
    const [filtroFechaFin, setFiltroFechaFin] = useState('');
    const [filtroFechaInicioTexto, setFiltroFechaInicioTexto] = useState('');
    const [filtroFechaFinTexto, setFiltroFechaFinTexto] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const pieRef = useRef(null);
    const barRef = useRef(null);
    const barTiempoRef = useRef(null);

    // UN SOLO EFECTO: Recarga todo desde el backend cuando cambian los filtros
    useEffect(() => {
        const cargarReportes = async () => {
            try {
                setLoading(true);
                const filtros = {
                    fechaDesde: filtroFechaInicio || null,
                    fechaHasta: filtroFechaFin || null,
                };

                const data = await reportesARTService.getAllReportes(filtros);

                setResumen(data.resumen);
                setPorEstado(data.porEstado);
                setTiempoPromedio(data.tiempoPromedio);
            } catch (err) {
                console.error('Error cargando reportes ART:', err);
            } finally {
                setLoading(false);
            }
        };
        cargarReportes();
    }, [filtroFechaInicio, filtroFechaFin]);

    // DATOS DE CHART.JS: Directos desde los arrays de estado
    // Torta: paleta del sistema, máximo 6 porciones (el resto en "Otros").
    const pieAgrupado = agruparEnOtros(porEstado.map(e => e.nombreEstado), porEstado.map(e => e.cantidad));
    const datosPie = {
        labels: pieAgrupado.labels,
        datasets: [{ data: pieAgrupado.data, backgroundColor: pieAgrupado.colores }]
    };

    const datosBar = {
        labels: porEstado.map(e => e.nombreEstado),
        datasets: [datasetBarrasSimple('Cantidad de trámites', porEstado.map(e => e.cantidad))]
    };

    const datosBarTiempo = {
        labels: tiempoPromedio.map(e => e.nombreEstado),
        datasets: [datasetBarrasSimple('Días promedio', tiempoPromedio.map(e => e.diasPromedio))]
    };

    // KPIs para el Header
    const total = resumen?.totalTramites ?? 0;
    const estadosActivos = resumen?.estadosActivos ?? 0;
    const nombreEstadoTop = resumen?.estadoMasFrecuenteNombre ?? '—';
    const cantEstadoTop = resumen?.estadoMasFrecuenteCantidad ?? 0;

    const limpiarFiltros = () => {
        setFiltroFechaInicio(''); setFiltroFechaFin('');
        setFiltroFechaInicioTexto(''); setFiltroFechaFinTexto('');
    };

    const { generarYDescargar } = usePDFReportes();

    const generarPDF = () => {
        generarYDescargar({
            tipo: 'ART',
            resumen,
            porEstado,
            tiempoPromedio,
            referencias: {
                pieRef,
                barRef,
                barTiempoRef
            }
        });
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight:'40vh' }}>
            <Spinner animation="border" style={{ color:'#F59E0B' }} />
            <span className="ms-3">Consultando reportes al servidor...</span>
        </div>
    );

    return (
        <div className="homepage-container area-art">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <span role="button" style={{ cursor:'pointer', color:'#3B82F6', textDecoration:'underline' }} onClick={()=>navigate('/reportes/tramites')}>
                            Reportes de Trámites
                        </span>
                    </li>
                    <li className="breadcrumb-item active">ART</li>
                </ol>
            </nav>

            <PageHeader
                variante="plano"
                icono="heart-pulse-fill"
                titulo="Reportes ART"
                descripcion="Accidentes de Trabajo — Análisis centralizado"
                accion={
                    <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={generarPDF}
                    >
                        <i className="bi bi-file-earmark-pdf" />
                        Exportar PDF
                    </button>
                }
            />

            {/* KPIs directos desde resumen */}
            <div className="ds-kpis">
                <TarjetaKpi label="Total trámites ART" valor={total} icono="cone-striped" tono="advertencia" />
                <TarjetaKpi label="Estados activos" valor={estadosActivos} icono="diagram-3" tono="advertencia" />
                <TarjetaKpi label="Estado más común" valor={nombreEstadoTop} sub={`${cantEstadoTop} trámites`} icono="bar-chart" tono="advertencia" esTexto />
            </div>

            {/* Filtros */}
            {(() => {
                const campoDesde = (
                    <Form.Group>
                        <Form.Label>Fecha Desde</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaInicioTexto || (filtroFechaInicio ? new Date(filtroFechaInicio+'T00:00:00').toLocaleDateString('es-AR') : '')}
                                onChange={e=>{ const fmt=formatearFechaMientrasEscribe(e.target.value); setFiltroFechaInicioTexto(fmt); const iso=convertirTextoAFecha(fmt); if(iso) setFiltroFechaInicio(iso); }}
                                style={{ flex:1 }}
                            />
                            <Form.Control type="date" value={filtroFechaInicio} onChange={e=>setFiltroFechaInicio(e.target.value)} style={{ width:'45px' }} />
                        </div>
                    </Form.Group>
                );
                const campoHasta = (
                    <Form.Group>
                        <Form.Label>Fecha Hasta</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaFinTexto || (filtroFechaFin ? new Date(filtroFechaFin+'T00:00:00').toLocaleDateString('es-AR') : '')}
                                onChange={e=>{ const fmt=formatearFechaMientrasEscribe(e.target.value); setFiltroFechaFinTexto(fmt); const iso=convertirTextoAFecha(fmt); if(iso) setFiltroFechaFin(iso); }}
                                style={{ flex:1 }}
                            />
                            <Form.Control type="date" value={filtroFechaFin} onChange={e=>setFiltroFechaFin(e.target.value)} style={{ width:'45px' }} />
                        </div>
                    </Form.Group>
                );

                const chipsFiltros = [
                    filtroFechaInicio && { key: 'desde', label: `Desde: ${filtroFechaInicioTexto || filtroFechaInicio}`, onQuitar: () => { setFiltroFechaInicio(''); setFiltroFechaInicioTexto(''); } },
                    filtroFechaFin && { key: 'hasta', label: `Hasta: ${filtroFechaFinTexto || filtroFechaFin}`, onQuitar: () => { setFiltroFechaFin(''); setFiltroFechaFinTexto(''); } },
                ].filter(Boolean);

                return esMobile ? (
                    <div className="eventos-section" style={{ marginBottom:'2rem', marginTop:'2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title"><i className="bi bi-funnel me-2" />Filtros</h2>
                        </div>
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar', onClick: limpiarFiltros } : undefined}
                            >
                                <div className="mb-3">{campoDesde}</div>
                                {campoHasta}
                            </BottomSheet>
                        )}
                    </div>
                ) : (
                    <div className="eventos-section" style={{ marginBottom:'2rem', marginTop:'2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title"><i className="bi bi-funnel me-2" />Filtros</h2>
                            <button type="button" className="btn-ver-calendario-home" onClick={limpiarFiltros}>Limpiar</button>
                        </div>
                        <Row>
                            <Col md={6}>{campoDesde}</Col>
                            <Col md={6}>{campoHasta}</Col>
                        </Row>
                    </div>
                );
            })()}

            {/* Gráficos */}
            <Row className="g-4 mb-4">
                <Col lg={6}>
                    <div className="eventos-section">
                        <h2 className="eventos-section-title">Distribución por estado</h2>
                        <p className="text-muted small mb-3">Cantidad de trámites ART agrupados por su estado actual, en el período filtrado.</p>
                        <Pie ref={pieRef} data={datosPie} options={BASE_OPTIONS} />
                    </div>
                </Col>
                <Col lg={6}>
                    <div className="eventos-section">
                        <h2 className="eventos-section-title">Cantidad por estado</h2>
                        <p className="text-muted small mb-3">Mismo desglose que el gráfico anterior, en barras para comparar cantidades entre estados.</p>
                        <Bar ref={barRef} data={datosBar} options={OPCIONES_BARRAS} />
                    </div>
                </Col>
            </Row>

            <div className="eventos-section" style={{ marginBottom:'2rem' }}>
                <h2 className="eventos-section-title">Tiempo promedio por estado</h2>
                <p className="text-muted small mb-3">Días promedio que un trámite permanece en cada estado antes de avanzar al siguiente.</p>
                <Bar ref={barTiempoRef} data={datosBarTiempo} options={OPCIONES_BARRAS_H} />
            </div>

            {/* Tabla detalle mapeando porEstado y tiempoPromedio */}
            <div className="eventos-section">
                <h2 className="eventos-section-title">Detalle por estado</h2>
                <p className="text-muted small mb-3">Cantidad, porcentaje sobre el total, tiempo promedio y clasificación de velocidad (Rápido/Normal/Lento) de cada estado.</p>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                        <tr>
                            <th>Estado</th>
                            <th className="text-center">Cantidad</th>
                            <th className="text-center">Porcentaje</th>
                            <th className="text-center">Tiempo Prom.</th>
                            <th className="text-center">Clasificación</th>
                        </tr>
                        </thead>
                        <tbody>
                        {porEstado.map((e) => {
                            const t = tiempoPromedio.find(tp => tp.nombreEstado === e.nombreEstado);
                            return (
                                <tr key={e.nombreEstado}>
                                    <td>{e.nombreEstado}</td>
                                    <td className="text-center fw-bold">{e.cantidad}</td>
                                    <td className="text-center">{e.porcentaje}%</td>
                                    <td className="text-center">{t ? `${t.diasPromedio} días` : '—'}</td>
                                    <td className="text-center">
                                            <span className={`badge ${t?.clasificacion === 'Rápido' ? 'bg-success' : t?.clasificacion === 'Normal' ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                                {t?.clasificacion || '—'}
                                            </span>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ReportesTramitesART;